import React, { useEffect, useState } from "react";
import TableCompo from "../Table/TableCompo";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  CardContent,
  Tooltip,
  Button,
  Modal,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery, useTheme,
} from "@mui/material";
import {
  getProductOrdersRequest,
  bookNowStatusChangeRequest,
  changeProdStatusRequest,
} from "../../../redux/ProductOrder/productOrdersSlice";
import { CiEdit } from "react-icons/ci";
import useCsvConverter from "../../../hooks/convertToCSV";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import { canShowPermission } from "../Permissions/permissionObject";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";


const ProductOrder = () => {
  const dispatch = useDispatch();
  const productOrders = useSelector((state) => state.productOrders || {});
  const { data = [], total = 0 } = productOrders;
  const [pagination, setPagination] = useState({ page: 0, limit: 5 });
  const [search, setSearch] = useState("");

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("");

  // booking dialogs
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
const [startDate, setStartDate] = useState(null);
const [endDate, setEndDate] = useState(null);

const formatDateTimeRange = (date, type) => {
  if (!date) return null;
  const d = new Date(date);
  if (type === "start") {
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (type === "end") {
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
};

  useEffect(() => {
    dispatch(
      getProductOrdersRequest({
        start: pagination.page * pagination.limit,
        limit: pagination.limit,
        search,
        startDate: formatDateTimeRange(startDate, "start"),
        endDate: formatDateTimeRange(endDate, "end"),
      }),
    );
  }, [dispatch, pagination, search]);

  const handleGetDetails = () => {
  if (!startDate || !endDate) {
    toast.error("Please select both start and end dates.");
    return;
  }
  if (startDate > endDate) {
    toast.error("Start Date cannot be after End Date");
    return;
  }

  setPagination((prev) => ({ ...prev, page: 0 }));
  dispatch(
    getProductOrdersRequest({
      start: 0,
      limit: pagination.limit,
      search,
      startDate: formatDateTimeRange(startDate, "start"),
      endDate: formatDateTimeRange(endDate, "end"),
    }),
  );
};

  const handleTableChange = (action, tableState) => {
    const dateParams =
    startDate && endDate
      ? {
          startDate: formatDateTimeRange(startDate, "start"),
          endDate: formatDateTimeRange(endDate, "end"),
        }
      : {};
    switch (action) {
      case "changePage":
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        dispatch(
        getProductOrdersRequest({
          start: tableState.page * pagination.limit,
          limit: pagination.limit,
          search,
          ...dateParams,
        }),
      );
        break;
      case "changeRowsPerPage":
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        dispatch(
        getProductOrdersRequest({
          start: 0,
          limit: tableState.rowsPerPage,
          search,
          ...dateParams,
        }),
      );
        break;
      case "search":
        setSearch(tableState.searchText || "");
        setPagination((prev) => ({ ...prev, page: 0 }));
        dispatch(
        getProductOrdersRequest({
          start: 0,
          limit: pagination.limit,
          search: tableState.searchText || "",
          ...dateParams,
        }),
      );
        break;
      default:
        break;
    }
  };

  const confirmBookingAction = () => {
    if (selectedId && selectedAction) {
      dispatch(
        bookNowStatusChangeRequest({
          id: selectedId ?? selectedOrderId,
          bookingstatus: selectedAction,
        }),
      );
    }
    setOpenDialog(false);
    setSelectedId(null);
    setSelectedAction(null);
  };

  const ApproveOrder = () => {
    if (!selectedOrderStatus) {
      alert("Please Select Status.");
      return;
    }

    dispatch(
      changeProdStatusRequest({
        order_id: selectedOrderId,
        status: selectedOrderStatus,
      }),
    );

    setStatusModalOpen(false);
    setSelectedOrderId(null);
    setSelectedOrderStatus("");

    dispatch(
      getProductOrdersRequest({
        start: pagination.page * pagination.limit,
        limit: pagination.limit,
        search,
      }),
    );
  };

  const decimal = useSelector(
        (state) => state?.appConfig?.configData?.decimalPrecision || 4,
      );

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);
  const canEdit = canShowPermission(permissions, "ProductOrder", "edit");

  const columns = [
    {
      field: "edit",
      headerName: "Edit",
      render: (val, row) => {
        const status = row.bookingstatus?.trim().toUpperCase();

        return status === "APPROVED" && canEdit ? (
          <CiEdit
            style={{
              cursor: "pointer",
              color: "#0a0b0cff",
              fontSize: "28px",
            }}
            onClick={() => {
              setSelectedOrderId(row._id);
              setSelectedOrderStatus("");
              setStatusModalOpen(true);
            }}
          />
        ) : status === "APPROVED" && !canEdit ? (
          <span style={{ fontSize: "12px", color: "#aaa" }}>No Permission</span>
        ) : (
          " "
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Date",
      render: (val, row) => {
        if (!row?.createdAt) return "N.A";
        const date = new Date(row.createdAt);
        const formattedDate = date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return (
          <Tooltip title={formattedDate} arrow>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "customer",
      headerName: "Name",
      render: (val, row) =>
        row.customer
          ? `${row.customer.firstName || ""} ${row.customer.lastName || ""}`
          : "N.A",
    },
    {
      field: "phone",
      headerName: "Phone",
      render: (val, row) =>
        row.customer ? `${row.customer.phone || ""}` : "N.A",
    },
    {
      field: "productdetails",
      headerName: "Product",
      render: (val, row) => row.productdetails?.name || "N.A",
    },
    {
      field: "price",
      headerName: "Price",
      render: (val, row) => Number(row?.amount || 0).toFixed(2),
    },
    {
      field: "weight",
      headerName: "Weight(Gms)",
      render: (val, row) => Number(row?.weight || 0).toFixed(decimal),
    },
    {
      field: "image",
      headerName: "Product Image",
      render: (val, row) => {
        if (row.productdetails?._id) {
          return (
            <Button
              onClick={() => {
                setSelectedProduct(row.productdetails);
                setOpenModal(true);
              }}
              variant="contained"
              color="warning"
              size="small"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: "bold",
              }}
            >
              View
            </Button>
          );
        } else {
          return "N.A";
        }
      },
    },
    {
      field: "bookingstatus",
      headerName: "Booking Status",
      render: (val, row) => {
        if (row.bookingstatus !== "PENDING") {
          switch (row.bookingstatus) {
            case "APPROVED":
              return (
                <span style={{ color: "green", fontWeight: "bold" }}>
                  APPROVED
                </span>
              );
            case "CANCELLED":
              return (
                <span style={{ color: "red", fontWeight: "bold" }}>
                  CANCELLED
                </span>
              );
            default:
              return "N.A";
          }
        } else {
          return (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={() => {
                setSelectedId(row._id);
                setSelectedAction(null);
                setActionDialogOpen(true);
              }}
            >
              Pending
            </Button>
          );
        }
      },
    },
    {
      field: "status",
      headerName: "Order Status",
      flex: 1,
      render: (val, row) => {
        const status = row.status;
        let color = "red";
        let text = status || "N.A";

        switch (status) {
          case "SUCCESS":
            color = "green";
            text = "ORDER DELIVERED";
            break;
          case "PENDING":
            color = "blue";
            text = "PENDING";
            break;
          case "DISPATCHED":
            color = "orange";
            text = "ORDER DISPATCHED";
            break;
          case "READYTODISPATCH":
            color = "orange";
            text = "READY TO DISPATCH";
            break;
          case "RETURNED":
            color = "red";
            text = "ORDER RETURNED";
            break;
          case "REFUNDCOMPLETED":
            color = "green";
            text = "REFUND COMPLETED";
            break;
        }

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              fontSize: "14px",
              fontWeight: "bold",
              color,
            }}
          >
            {text}
          </div>
        );
      },
    },
    {
      field: "approvedby",
      headerName: "Updated By",
      render: (val, row) => row.approvedBy || "N.A",
    },
  ];
  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    if (!startDate || !endDate) {
    toast.error("Please select both start and end dates.");
    return;
  }
  if (startDate > endDate) {
    toast.error("Start Date cannot be after End Date");
    return;
  }
    const query = {
    startDate: formatDateTimeRange(startDate, "start"),
    endDate: formatDateTimeRange(endDate, "end"),
  };
    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/product/productOrdersCsv",
        query,
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "productOrders.csv");
        },
      }),
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          Product Orders
        </Typography>

        <CardContent sx={{ height: "100%", width: "100%" }}>
          <Box display="flex" gap={1.5} px={2} pb={1} flexWrap="wrap" alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={1.5} px={2} py={1}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  maxDate={endDate || new Date()}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        minWidth: 160,
                        "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                        "& .MuiInputLabel-root": { fontSize: 13 },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        minWidth: 160,
                        "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                        "& .MuiInputLabel-root": { fontSize: 13 },
                      },
                    },
                    }}
                  />
                  <Button
                    variant="outlined"
                    fullWidth={isMobile}
                    sx={{
                      height: 36,
                      minWidth: 120,
                      fontSize: 13,
                      textTransform: "none",
                      px: 2,
                      whiteSpace: "nowrap",
                    }}
                      onClick={handleGetDetails}
                  >
                    Get Details
                  </Button>
                </Box>
              </LocalizationProvider>
          </Box>
          <TableCompo
            columns={columns}
            data={JSON.parse(JSON.stringify(data))}
            totalCount={total}
            onTableChange={handleTableChange}
            page={pagination.page}
            limit={pagination.limit}
            extraButtons={
              <Button
                size="sm"
                variant="outlined"
                onClick={handleDownloadStoreCsv}
              >
                Download CSV
              </Button>
            }
          />

          <Modal open={openModal} onClose={() => setOpenModal(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                maxWidth: 500,
                textAlign: "center",
              }}
            >
              {selectedProduct ? (
                <>
                  <img
                    src={selectedProduct.url}
                    alt={selectedProduct.name}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      maxHeight: "400px",
                      objectFit: "contain",
                      marginBottom: "16px",
                    }}
                  />
                  <Typography variant="h6" fontWeight="bold">
                    {selectedProduct.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedProduct.desc}
                  </Typography>
                  <Typography variant="subtitle1" color="primary" mt={1}>
                    ₹{selectedProduct.mrp}
                  </Typography>
                </>
              ) : (
                <p>No product details available</p>
              )}
            </Box>
          </Modal>

          <Modal
            open={statusModalOpen}
            onClose={() => setStatusModalOpen(false)}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                minWidth: 400,
              }}
            >
              <Typography variant="h6" mb={2}>
                Product Order Details
              </Typography>

              <select
                value={selectedOrderStatus}
                onChange={(e) => setSelectedOrderStatus(e.target.value)}
                style={{ width: "100%", padding: "8px", marginBottom: "16px" }}
              >
                <option value="">Select Status</option>
                <option value="READYTODISPATCH">READY TO DISPATCH</option>
                <option value="DISPATCHED">ORDER DISPATCHED</option>
                <option value="APPROVED">ORDER DELIVERED</option>
                <option value="RETURNED">ORDER RETURNED</option>
                <option value="REFUNDCOMPLETED">REFUND COMPLETED</option>
              </select>

              <Button
                variant="contained"
                onClick={() => ApproveOrder()}
                fullWidth
              >
                Submit
              </Button>
            </Box>
          </Modal>

          <Dialog
            open={actionDialogOpen}
            onClose={() => setActionDialogOpen(false)}
          >
            <DialogTitle>Update Booking Status</DialogTitle>
            <DialogContent>
              <Typography>
                Please choose whether to <strong>Approve</strong> or{" "}
                <strong>Cancel</strong> this booking .
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setActionDialogOpen(false)}
                color="secondary"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setSelectedAction("APPROVED");
                  setActionDialogOpen(false);
                  setOpenDialog(true);
                }}
                variant="contained"
                color="success"
              >
                Approve
              </Button>
              <Button
                onClick={() => {
                  setSelectedAction("CANCELLED");
                  setActionDialogOpen(false);
                  setOpenDialog(true);
                }}
                variant="contained"
                color="error"
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogContent>
              Are you sure you want to{" "}
              <strong
                style={{
                  color: selectedAction === "APPROVED" ? "green" : "red",
                }}
              >
                {selectedAction}
              </strong>{" "}
              this booking?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={confirmBookingAction}
                variant="contained"
                color={selectedAction === "APPROVED" ? "success" : "error"}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Box>
    </Box>
  );
};

export default ProductOrder;
